import { LockIcon } from '@/pages/fulfillment/components/CancelLockIcon';
import { cancelLockOf, describeCancelLock, type CancelLockFields } from '@/utils/fulfillmentCancelLock';
import './cancelLockBadge.css';

/**
 * "Cancel locked" (SSD-7450): from this moment a change-of-mind cancel is refused, while our side can still cancel.
 * Nothing renders for an order that is not locked, or on a server that does not send the lock yet.
 *
 * `header` sits on the Order Detail title line as two short lines — the words over the KST time and the source — so
 * it stays inside the line's height and leaves the Shelf box where it was. `row` lives in the Orders list's
 * fixed-width status cell, which has 144px of text: the words "Cancel locked", the time and the source each take a
 * line of their own, so a reader of the list sees what the box means without hovering and no column widens.
 */
export const CancelLockBadge = ({ lock: fields, variant }: { lock: CancelLockFields; variant: 'header' | 'row' }) => {
  const lock = cancelLockOf(fields);
  if (lock === null) {
    return null;
  }
  const sentence = describeCancelLock(lock);
  if (variant === 'row') {
    return (
      <span className="ff-cancel-lock is-row" title={sentence} data-testid="cancel-lock-badge">
        <span className="cl-line">
          <LockIcon />
          <span className="cl-head">Cancel locked</span>
        </span>{' '}
        <span className="cl-when">{lock.at}</span>
        {lock.source && (
          <>
            {' '}
            <span className="cl-src">{lock.source}</span>
          </>
        )}
      </span>
    );
  }
  return (
    <span className="ff-cancel-lock is-header" title={sentence} data-testid="cancel-lock-badge">
      <LockIcon />
      <span className="cl-text">
        <span className="cl-head">Cancel locked</span>
        <span className="cl-meta">
          <span className="cl-when">{lock.at}</span>
          {lock.source && (
            <>
              {' · '}
              <span className="cl-src">{lock.source}</span>
            </>
          )}
        </span>
      </span>
    </span>
  );
};
